import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { timeClockAPI, taskAPI, announcementAPI, restaurantGroupAPI, shiftSwapAPI, stripeAPI, timesheetAPI, aiAPI } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { formatTime, formatDate, getStatusColor } from '../lib/utils';
import { LaborDashboard } from '../components/LaborDashboard';
import OnboardingChecklist from '../components/OnboardingChecklist';
import LifetimeUpsellCard from '../components/LifetimeUpsellCard';
import DemoUpgradeCTA from '../components/DemoUpgradeCTA';
import DemoActivityFeed from '../components/DemoActivityFeed';
import { ReplacementWeekCard } from '../components/dashboard/ReplacementWeekCard';
import {
  Clock,
  Calendar,
  CheckSquare,
  Megaphone,
  Loader2,
  Play,
  Square,
  InboxIcon,
  ArrowLeftRight,
  ClipboardEdit,
  Check,
  X,
  Sparkles,
  Tag,
  ArrowRight,
  DollarSign,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// TrialUpgradeBanner — Shows trial days remaining + upgrade CTA
// ─────────────────────────────────────────────────────────────
function TrialUpgradeBanner({ restaurantGroup }) {
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // Calculate days remaining
  const trialEnd = restaurantGroup?.trial_ends_at || restaurantGroup?.trial_expiry;
  const daysRemaining = trialEnd
    ? Math.max(0, Math.ceil((new Date(trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))
    : 30;

  const planType = restaurantGroup?.plan_type || 'single';
  const billingCycle = restaurantGroup?.billing_cycle || 'monthly';

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const { data } = await stripeAPI.validateCoupon({
        coupon_code: couponCode.trim(),
        plan_type: planType,
        billing_cycle: billingCycle,
      });
      setCouponResult(data);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid or expired promo code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponResult(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleUpgradeClick = () => {
    // Navigate to billing page with coupon info if applied
    if (couponResult) {
      sessionStorage.setItem('pendingCoupon', JSON.stringify(couponResult));
    }
    navigate('/billing');
  };

  // Determine urgency
  const isUrgent = daysRemaining <= 7;
  const bgColor = isUrgent
    ? 'bg-gradient-to-r from-orange-500 to-orange-600'
    : 'bg-gradient-to-r from-slate-800 to-slate-900';

  return (
    <div
      className={`${bgColor} rounded-xl p-5 text-white shadow-lg relative overflow-hidden`}
      data-testid="trial-upgrade-banner"
    >
      {/* Background sparkle effect */}
      <div className="absolute top-0 right-0 opacity-10">
        <Sparkles size={120} />
      </div>

      <div className="relative z-10">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isUrgent ? 'bg-white/20' : 'bg-orange-500/80'}`}>
              <Clock size={20} />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                {daysRemaining === 0
                  ? 'Your trial ends today!'
                  : daysRemaining === 1
                    ? '1 day left in your trial'
                    : `${daysRemaining} days left in your trial`}
              </h3>
              <p className="text-sm text-white/80">
                {isUrgent
                  ? 'Upgrade now to keep all your data and features'
                  : 'Subscribe before your trial ends to continue using ReStaff'}
              </p>
            </div>
          </div>
        </div>

        {/* Coupon Section */}
        <div className="mb-4">
          {couponResult ? (
            <div
              className="flex items-center justify-between p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg"
              data-testid="dashboard-coupon-applied"
            >
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-300 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {couponResult.coupon_code} — <strong>{couponResult.discount_display}</strong>
                </span>
              </div>
              <button
                type="button"
                onClick={removeCoupon}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="dashboard-remove-coupon"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-white/70 mb-1">
                <Tag size={14} />
                <span>Have a promo code?</span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter promo code"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal"
                  data-testid="dashboard-coupon-input"
                  maxLength={50}
                  autoComplete="off"
                />
                <Button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  className="shrink-0 bg-white/20 hover:bg-white/30 text-white border-0"
                  data-testid="dashboard-apply-coupon"
                >
                  {couponLoading ? <Loader2 size={15} className="animate-spin" /> : 'Apply'}
                </Button>
              </div>
              {couponError && (
                <p className="text-xs text-red-200 flex items-center gap-1" data-testid="dashboard-coupon-error">
                  <AlertCircle size={12} className="flex-shrink-0" />
                  {couponError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* CTA Button */}
        <Button
          onClick={handleUpgradeClick}
          disabled={upgradeLoading}
          className={`w-full sm:w-auto ${
            isUrgent
              ? 'bg-white text-orange-600 hover:bg-orange-50'
              : 'bg-orange-500 hover:bg-orange-600 text-white'
          } font-semibold px-6 py-2.5`}
          data-testid="dashboard-upgrade-btn"
        >
          {upgradeLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              Upgrade Now
              {couponResult && ` (${couponResult.discount_display})`}
              <ArrowRight size={16} className="ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RequestsTab — aggregates pending shift swaps + time approvals
// Visible only to owner / manager / super_admin
// ─────────────────────────────────────────────────────────────
function RequestsTab({ locations }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [acting, setActing]     = useState({});   // { [id]: true } while API in-flight

  // Map restaurant_id → location_name for display
  const locationMap = locations.reduce((acc, l) => { acc[l.id] = l.location_name; return acc; }, {});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [swapsRes, shiftsRes] = await Promise.all([
        shiftSwapAPI.getAll('pending').catch(() => ({ data: [] })),
        timeClockAPI.getShifts({ status: 'pending_approval' }).catch(() => ({ data: [] })),
      ]);

      const swaps = (swapsRes.data || []).map(s => ({
        id:       s.id,
        employee: s.requester_name || '—',
        type:     'Shift Swap',
        typeIcon: 'swap',
        date:     s.created_at,
        location: locationMap[s.restaurant_id] || s.restaurant_id || '—',
        status:   s.status || 'pending',
        raw:      s,
      }));

      const timeEdits = (shiftsRes.data || []).map(s => ({
        id:       s.id,
        employee: s.user_name || '—',
        type:     s.edited_flag ? 'Time Edit' : 'Time Approval',
        typeIcon: 'clock',
        date:     s.start_time,
        location: locationMap[s.restaurant_id] || s.restaurant_id || '—',
        status:   'pending',
        raw:      s,
      }));

      // Most recent first
      const all = [...swaps, ...timeEdits].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setItems(all);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (item, action) => {
    setActing(prev => ({ ...prev, [item.id]: true }));
    try {
      if (item.typeIcon === 'swap') {
        await shiftSwapAPI.respond(item.id, action === 'approve' ? 'accept' : 'reject');
      } else {
        if (action === 'approve') {
          await timeClockAPI.approveShift(item.id);
        } else {
          toast('Rejection not available for time approvals — edit the shift directly.', { icon: 'ℹ️' });
          setActing(prev => ({ ...prev, [item.id]: false }));
          return;
        }
      }
      toast.success(`${item.type} ${action === 'approve' ? 'approved' : 'rejected'}`);
      await fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActing(prev => ({ ...prev, [item.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-orange-500 mr-2" size={20} />
        <span className="text-slate-500 text-sm">Loading requests…</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="flex flex-col items-center justify-center py-14 gap-3">
          <InboxIcon size={36} className="text-slate-300" />
          <p className="text-slate-500 font-medium">No pending requests</p>
          <p className="text-xs text-slate-400">Shift swap and time approval requests will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  const typeBadge = (type, icon) => {
    const base = 'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full';
    if (icon === 'swap')  return <span className={`${base} bg-purple-100 text-purple-700`}><ArrowLeftRight size={11} />{type}</span>;
    return                       <span className={`${base} bg-blue-100 text-blue-700`}><ClipboardEdit size={11} />{type}</span>;
  };

  const statusBadge = (status) => {
    const map = {
      pending:  'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${map[status] || 'bg-slate-100 text-slate-600'}`}>
        {status}
      </span>
    );
  };

  return (
    <Card className="border-slate-200" data-testid="requests-table-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <InboxIcon size={17} className="text-orange-500" />
          Pending Requests
          <span className="ml-1 text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">
            {items.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">

        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Employee</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Type</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Date</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Location</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Status</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id} className={`border-b border-slate-100 hover:bg-slate-50/40 transition-colors ${idx % 2 === 0 ? '' : 'bg-slate-50/20'}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{item.employee}</td>
                  <td className="px-4 py-3">{typeBadge(item.type, item.typeIcon)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(item.date)}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{item.location}</td>
                  <td className="px-4 py-3">{statusBadge(item.status)}</td>
                  <td className="px-4 py-3">
                    {item.status === 'pending' && (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          className="h-7 px-2.5 text-xs bg-green-500 hover:bg-green-600 text-white"
                          disabled={!!acting[item.id]}
                          onClick={() => handleAction(item, 'approve')}
                          data-testid={`approve-${item.id}`}
                        >
                          {acting[item.id] ? <Loader2 size={11} className="animate-spin" /> : <><Check size={11} className="mr-1" />Approve</>}
                        </Button>
                        {item.typeIcon === 'swap' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-xs border-red-200 text-red-600 hover:bg-red-50"
                            disabled={!!acting[item.id]}
                            onClick={() => handleAction(item, 'reject')}
                            data-testid={`reject-${item.id}`}
                          >
                            <X size={11} className="mr-1" />Reject
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden divide-y divide-slate-100">
          {items.map(item => (
            <div key={item.id} className="px-4 py-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800 text-sm">{item.employee}</span>
                {statusBadge(item.status)}
              </div>
              <div className="flex flex-wrap gap-2 items-center text-xs text-slate-500">
                {typeBadge(item.type, item.typeIcon)}
                <span>{formatDate(item.date)}</span>
                <span className="truncate max-w-[120px]">{item.location}</span>
              </div>
              {item.status === 'pending' && (
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    className="flex-1 h-8 text-xs bg-green-500 hover:bg-green-600 text-white"
                    disabled={!!acting[item.id]}
                    onClick={() => handleAction(item, 'approve')}
                  >
                    {acting[item.id] ? <Loader2 size={12} className="animate-spin" /> : 'Approve'}
                  </Button>
                  {item.typeIcon === 'swap' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                      disabled={!!acting[item.id]}
                      onClick={() => handleAction(item, 'reject')}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations, locations } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [clockLoading, setClockLoading] = useState(false);
  const [activeShift, setActiveShift] = useState(null);
  const [stats, setStats] = useState({
    todayTasks: 0,
    completedTasks: 0,
    announcements: 0,
    staffCount: 0,
    scheduledToday: 0,
  });
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [restaurantGroup, setRestaurantGroup] = useState(null);
  const [overtimeAlerts, setOvertimeAlerts] = useState(null); // null = not loaded yet
  const [evaInsight, setEvaInsight] = useState('');
  const [weeklyLaborCost, setWeeklyLaborCost] = useState(null); // null = loading
  const [weeklyPayroll, setWeeklyPayroll] = useState({ employees: [], period_totals: {} });
  const [weeklyEntries, setWeeklyEntries] = useState([]);
  const { formatMoney } = useCurrency();

  useEffect(() => { document.title = 'Dashboard | ReStaff'; }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [user, selectedLocationId]);

  const fetchDashboardData = async () => {
    const locId = isAllLocations ? user?.restaurant_id : selectedLocationId;
    if (!locId) {
      setLoading(false);
      return;
    }

    try {
      const [shiftRes, tasksRes, announcementsRes] = await Promise.all([
        timeClockAPI.getActiveShift(),
        taskAPI.getAll(locId),
        announcementAPI.getAll(locId),
      ]);

      setActiveShift(shiftRes.data);

      const tasks = tasksRes.data || [];
      setTodayTasks(tasks.slice(0, 5));
      setStats(prev => ({
        ...prev,
        todayTasks: tasks.length,
        completedTasks: tasks.filter(t => t.status === 'completed').length,
      }));

      const announcements = announcementsRes.data || [];
      setRecentAnnouncements(announcements.slice(0, 3));
      setStats(prev => ({ ...prev, announcements: announcements.length }));

      // Get restaurant group info if owner
      if (canManage) {
        try {
          const groupRes = await restaurantGroupAPI.getMine();
          setRestaurantGroup(groupRes.data);
        } catch (e) {
          // Group might not exist yet
        }

        // Fetch this week's timesheet entries for overtime alerts (no new endpoints)
        try {
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          const today = new Date().toISOString().split('T')[0];
          const weekStartStr = weekStart.toISOString().split('T')[0];
          const entriesRes = await timesheetAPI.getEntries({ start_date: weekStartStr, end_date: today });
          const weekEntries = entriesRes.data || [];
          setWeeklyEntries(weekEntries);

          // Compute per-employee weekly totals
          const byEmp = {};
          weekEntries.forEach(e => {
            const key = e.employee_id || e.employee_name;
            if (!byEmp[key]) byEmp[key] = { name: e.employee_name, hours: 0, maxDaily: 0, missingOut: false };
            byEmp[key].hours += e.duration_hours || 0;
            byEmp[key].maxDaily = Math.max(byEmp[key].maxDaily, e.duration_hours || 0);
            if (!e.clock_out) byEmp[key].missingOut = true;
          });

          const employees = Object.values(byEmp);
          const alerts = {
            weeklyOt:    employees.filter(e => e.hours > 40).length,
            longShifts:  employees.filter(e => e.maxDaily > 10).length,
            nearOt:      employees.filter(e => e.maxDaily > 8 && e.maxDaily <= 10).length,
            missingOut:  employees.filter(e => e.missingOut).length,
            topRisks:    employees
              .filter(e => e.hours > 40 || e.maxDaily > 8 || e.missingOut)
              .sort((a, b) => b.hours - a.hours)
              .slice(0, 3),
          };
          setOvertimeAlerts(alerts);

          // Fetch weekly labor cost for Budget Tracker (reuse weekStartStr/today already computed)
          try {
            const payrollRes = await timesheetAPI.getPayrollSummary({ start_date: weekStartStr, end_date: today });
            setWeeklyLaborCost(payrollRes.data?.period_totals?.total_pay ?? 0);
            setWeeklyPayroll(payrollRes.data || { employees: [], period_totals: {} });
          } catch {
            setWeeklyLaborCost(0);
          }

          const hasRisks = alerts.weeklyOt > 0 || alerts.missingOut > 0 || alerts.longShifts > 0 || alerts.nearOt > 0;
          if (!hasRisks) {
            setEvaInsight('No major risks detected.');
          } else {
            const CACHE_KEY = 'eva_insight_cache_v1';
            const TTL = 30 * 60 * 1000;

            // Check cache first
            try {
              const cached = localStorage.getItem(CACHE_KEY);
              if (cached) {
                const parsed = JSON.parse(cached);
                if (Date.now() - parsed.timestamp < TTL) {
                  setEvaInsight(parsed.text);
                  return;
                }
              }
            } catch { /* ignore parse errors */ }

            // Cache miss — call Eva
            try {
              const overtime_risks = employees
                .filter(e => e.hours > 40 || e.maxDaily > 8 || e.missingOut)
                .sort((a, b) => b.hours - a.hours)
                .slice(0, 5)
                .map(e => ({
                  name: e.name,
                  hours_today: Math.round(e.maxDaily * 100) / 100,
                  hours_week:  Math.round(e.hours * 100) / 100,
                  risk_level:  e.missingOut ? 'missing_clockout'
                    : e.hours > 40   ? 'weekly_ot'
                    : e.maxDaily > 10 ? 'long_shift'
                    : 'near_ot',
                }));
              const res = await aiAPI.chat(
                'Give 1 short actionable insight about these overtime risks. Max 15 words. No intro. One sentence only.',
                { overtime_risks }
              );
              const text = res.data?.response || 'Overtime risks detected. Review timesheets.';
              localStorage.setItem(CACHE_KEY, JSON.stringify({ text, timestamp: Date.now() }));
              setEvaInsight(text);
            } catch {
              setEvaInsight('Overtime risks detected. Review timesheets.');
            }
          }
        } catch (e) {
          setOvertimeAlerts({ weeklyOt: 0, longShifts: 0, nearOt: 0, missingOut: 0, topRisks: [] });
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockAction = async () => {
    setClockLoading(true);
    try {
      if (activeShift) {
        await timeClockAPI.clockOut();
        toast.success('Clocked out successfully!');
        setActiveShift(null);
      } else {
        const response = await timeClockAPI.clockIn();
        toast.success('Clocked in successfully!');
        setActiveShift({ start_time: response.data.start_time });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Clock action failed');
    } finally {
      setClockLoading(false);
    }
  };

  const handleTaskToggle = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await taskAPI.update(task.id, { status: newStatus });
      
      setTodayTasks(prev => 
        prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t)
      );
      
      setStats(prev => ({
        ...prev,
        completedTasks: prev.completedTasks + (newStatus === 'completed' ? 1 : -1),
      }));

      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task marked pending');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 break-words" style={{ fontFamily: 'Manrope' }}>
              {t('dashboard.welcome', { name: user?.name?.split(' ')[0] })}
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          
          {restaurantGroup && (
            <div className={`status-badge ${getStatusColor(restaurantGroup.status)} shrink-0`}>
              {restaurantGroup.status === 'trial' ? '30-Day Trial' : restaurantGroup.status.charAt(0).toUpperCase() + restaurantGroup.status.slice(1)}
            </div>
          )}
        </div>

        {/* Demo conversion CTA — renders only for demo users; null otherwise */}
        <DemoUpgradeCTA />

        {/* Trial Upgrade Banner — Only for owners/managers on trial */}
        {canManage && restaurantGroup?.status === 'trial' && (
          <TrialUpgradeBanner restaurantGroup={restaurantGroup} />
        )}

        {/* Onboarding Checklist — auto-hides when all 4 steps complete */}
        {canManage && <OnboardingChecklist />}

        {/* Lifetime Deal upsell — auto-hides for non-LTD / unlimited / under-cap tenants */}
        {canManage && <LifetimeUpsellCard />}

        {/* Tab bar — Managers/Owners/Super Admin only */}
        {canManage && (
          <div className="flex gap-1 border-b border-slate-200 overflow-x-auto scrollbar-hide">
            {[
              { id: 'overview', label: t('dashboard.overview') },
              { id: 'labor', label: t('dashboard.labor') },
              { id: 'requests', label: t('dashboard.requests') },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Requests tab ── */}
        {canManage && activeTab === 'requests' && (
          <RequestsTab locations={locations} />
        )}

        {/* ── Labor tab ── */}
        {canManage && activeTab === 'labor' && (
          <LaborDashboard payrollData={weeklyPayroll} entries={weeklyEntries} />
        )}

        {/* ── Overview tab (or default for staff) ── */}
        {(activeTab === 'overview' || !canManage) && (
          <>
        {/* Clock In/Out Card */}
        <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="text-center md:text-left w-full md:w-auto">
                <p className="text-slate-300 text-sm mb-1">{t('dashboard.currentStatus')}</p>
                <p className="text-xl sm:text-2xl font-bold">
                  {activeShift ? t('dashboard.onShift') : t('dashboard.offShift')}
                </p>
                {activeShift && (
                  <p className="text-slate-300 text-sm mt-1">
                    {t('dashboard.startedAt')} {formatTime(activeShift.start_time)}
                  </p>
                )}
              </div>
              
              <Button
                size="lg"
                onClick={handleClockAction}
                disabled={clockLoading}
                className={`${
                  activeShift 
                    ? 'bg-white text-slate-900 hover:bg-slate-100' 
                    : 'bg-orange-500 hover:bg-orange-600'
                } font-bold text-lg px-8 py-6`}
                data-testid="clock-action-btn"
              >
                {clockLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : activeShift ? (
                  <>
                    <Square className="mr-2 h-5 w-5" fill="currentColor" />
                    {t('dashboard.clockOut')}
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-5 w-5" fill="currentColor" />
                    {t('dashboard.clockIn')}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckSquare className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.completedTasks}/{stats.todayTasks}</p>
                  <p className="text-sm text-slate-500">{t('dashboard.tasksDone')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Megaphone className="text-orange-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.announcements}</p>
                  <p className="text-sm text-slate-500">{t('dashboard.announcements')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Clock className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {activeShift ? 'Active' : '—'}
                  </p>
                  <p className="text-sm text-slate-500">{t('dashboard.shiftStatus')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Date().toLocaleDateString('en-US', { weekday: 'short' })}
                  </p>
                  <p className="text-sm text-slate-500">{t('dashboard.today')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Shift Replacement — manager-only weekly operational snapshot */}
        {canManage && <ReplacementWeekCard />}

        {/* Eva Insight — one AI sentence, no chat required */}
        {canManage && evaInsight && (
          <Card className="border-violet-200 bg-violet-50/30" data-testid="eva-insight-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles size={13} className="text-violet-500" />
                <span className="text-xs font-semibold text-violet-600 uppercase tracking-wide">{t('dashboard.evaInsight')}</span>
              </div>
              <p className="text-sm text-slate-700 leading-snug" data-testid="eva-insight-text">
                {evaInsight}
              </p>
            </CardContent>
          </Card>
        )}

        {canManage && !evaInsight && overtimeAlerts !== null && (
          <Card className="border-violet-100 bg-violet-50/20" data-testid="eva-insight-card-loading">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Sparkles size={13} className="text-violet-400 animate-pulse" />
                <span className="text-xs text-violet-500">Eva Analyzing…</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overtime Alerts — owners & managers only */}
        {canManage && overtimeAlerts !== null && (
          <Card
            className={`border cursor-pointer transition-colors ${
              overtimeAlerts.weeklyOt > 0 || overtimeAlerts.missingOut > 0
                ? 'border-red-200 bg-red-50/40 hover:border-red-300'
                : overtimeAlerts.nearOt > 0 || overtimeAlerts.longShifts > 0
                ? 'border-amber-200 bg-amber-50/40 hover:border-amber-300'
                : 'border-emerald-200 bg-emerald-50/30 hover:border-emerald-300'
            }`}
            onClick={() => window.location.href = '/timesheets'}
            data-testid="overtime-alerts-card"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  overtimeAlerts.weeklyOt > 0 || overtimeAlerts.missingOut > 0
                    ? 'bg-red-100'
                    : overtimeAlerts.nearOt > 0
                    ? 'bg-amber-100'
                    : 'bg-emerald-100'
                }`}>
                  <AlertTriangle size={18} className={
                    overtimeAlerts.weeklyOt > 0 || overtimeAlerts.missingOut > 0
                      ? 'text-red-600'
                      : overtimeAlerts.nearOt > 0
                      ? 'text-amber-600'
                      : 'text-emerald-600'
                  } />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700 mb-1">Overtime Alerts</p>
                  {overtimeAlerts.weeklyOt === 0 && overtimeAlerts.missingOut === 0 && overtimeAlerts.nearOt === 0 && overtimeAlerts.longShifts === 0 ? (
                    <p className="text-sm text-emerald-700">No overtime concerns this week</p>
                  ) : (
                    <div className="space-y-0.5">
                      {overtimeAlerts.weeklyOt > 0 && (
                        <p className="text-sm text-red-700 font-medium" data-testid="ot-weekly-count">
                          {overtimeAlerts.weeklyOt} exceeded 40h this week
                        </p>
                      )}
                      {overtimeAlerts.missingOut > 0 && (
                        <p className="text-sm text-red-600" data-testid="ot-missing-count">
                          {overtimeAlerts.missingOut} missing clock-out
                        </p>
                      )}
                      {overtimeAlerts.longShifts > 0 && (
                        <p className="text-sm text-orange-700" data-testid="ot-longshift-count">
                          {overtimeAlerts.longShifts} long shift{overtimeAlerts.longShifts > 1 ? 's' : ''} (&gt;10h)
                        </p>
                      )}
                      {overtimeAlerts.nearOt > 0 && (
                        <p className="text-sm text-amber-700" data-testid="ot-nearot-count">
                          {overtimeAlerts.nearOt} near overtime (&gt;8h)
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-1.5">View Timesheets →</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Budget Tracker — weekly labor cost vs localStorage budget */}
        {canManage && weeklyLaborCost !== null && (() => {
          const budget = Number(localStorage.getItem('payroll_budget_weekly_v1') || 0);
          const variance = weeklyLaborCost - budget;
          const pct = budget > 0 ? ((variance / budget) * 100).toFixed(1) : 0;
          const isOver = variance > 0;
          return (
            <Card className="border-slate-200" data-testid="budget-tracker-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={13} className="text-slate-500" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Budget Tracker</span>
                </div>
                {budget === 0 ? (
                  <p className="text-sm text-slate-400">
                    No budget set.{' '}
                    <a href="/timesheets" className="underline text-violet-500 hover:text-violet-700">Set it in Payroll Analytics</a>
                  </p>
                ) : (
                  <div className="space-y-1 text-sm" data-testid="budget-tracker-detail">
                    <div className="flex justify-between text-slate-500">
                      <span>Budget</span>
                      <span data-testid="budget-tracker-budget">{formatMoney(budget)}</span>
                    </div>
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Actual (this week)</span>
                      <span data-testid="budget-tracker-actual">{weeklyLaborCost > 0 ? formatMoney(weeklyLaborCost) : '—'}</span>
                    </div>
                    <div className={`flex justify-between font-semibold pt-1 border-t border-slate-100 ${isOver ? 'text-red-600' : 'text-emerald-600'}`}
                      data-testid="budget-tracker-variance">
                      <span>Variance</span>
                      <span>{isOver ? '+' : ''}{formatMoney(variance)} ({isOver ? '+' : ''}{pct}%)</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Live Activity (demo tenant only — internally renders null otherwise) */}
        <DemoActivityFeed />

        {/* Tasks and Announcements */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Today's Tasks */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckSquare size={20} className="text-slate-600" />
                {t('dashboard.todayTasks')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {todayTasks.length === 0 ? (
                <div className="text-center py-6" data-testid="dashboard-tasks-empty-state">
                  <p className="text-sm text-slate-500 font-medium">{t('dashboard.noTasks')}</p>
                  <p className="text-xs text-slate-400 mt-1 mb-3">Tasks assigned for today will appear here</p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/tasks')} data-testid="dashboard-tasks-empty-cta">
                    + View Tasks
                  </Button>
                </div>
              ) : (
                todayTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`task-item ${task.status === 'completed' ? 'completed' : ''}`}
                    onClick={() => handleTaskToggle(task)}
                    data-testid={`task-${task.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${
                      task.status === 'completed' 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'border-slate-300'
                    }`}>
                      {task.status === 'completed' && <CheckSquare size={14} />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                        {task.title}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        task.type === 'opening' ? 'bg-blue-100 text-blue-700' :
                        task.type === 'closing' ? 'bg-purple-100 text-purple-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {task.type}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Announcements */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Megaphone size={20} className="text-slate-600" />
                {t('dashboard.recentAnnouncements')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {recentAnnouncements.length === 0 ? (
                <div className="text-center py-6" data-testid="dashboard-announcements-empty-state">
                  <p className="text-sm text-slate-500 font-medium">{t('dashboard.noAnnouncements')}</p>
                  <p className="text-xs text-slate-400 mt-1 mb-3">Share updates with your team to keep everyone informed</p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/announcements')} data-testid="dashboard-announcements-empty-cta">
                    + Post Announcement
                  </Button>
                </div>
              ) : (
                recentAnnouncements.map((announcement) => (
                  <div key={announcement.id} className="border-b border-slate-100 pb-3 last:border-0">
                    <h4 className="font-medium text-slate-900">{announcement.title}</h4>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{announcement.message}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      By {announcement.created_by_name} • {formatDate(announcement.created_at)}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
          </>
        )}
      </div>
    </Layout>
  );
}
